/**
 * Version-Gate: erkennt veraltete App-Bundles (Browser-/PWA-Cache) und erzwingt
 * einen Reload, bevor ein alter Client ohne die Datenverlust-Guards vom
 * 21.07.2026 weiter schreiben kann. Der Build legt /version.json mit der
 * deployten package.json-Version ab (vite.config.ts); beim Fokussieren der App
 * wird sie gedrosselt abgeglichen.
 */

export interface VersionGateOptions {
    currentVersion: string;
    /** Build-Zeitstempel des laufenden Bundles (__BUILD_TIME__) — erkennt auch
     *  Deployments ohne Versions-Bump in package.json. */
    currentBuildTime?: string;
    onOutdated: () => void;
    fetchImpl?: typeof fetch;
    /** Mindestabstand zwischen zwei echten Checks. Default 5 Minuten. */
    minIntervalMs?: number;
    /** So lange darf der Abruf von /version.json dauern. Default 10 Sekunden. */
    fetchTimeoutMs?: number;
    now?: () => number;
    versionUrl?: string;
}

/**
 * true nur, wenn `deployed` eine echt neuere Semver-Version als `current` ist.
 * Unparsebare Werte ergeben false — ein kaputtes version.json darf niemals
 * Reload-Schleifen ausloesen.
 */
export const isNewerVersion = (current: string, deployed: string | undefined | null): boolean => {
    if (!current || !deployed) return false;
    const parse = (v: string): number[] | null => {
        const parts = v.trim().split('.');
        const nums = parts.map(p => Number(p));
        if (nums.some(n => !Number.isFinite(n) || n < 0)) return null;
        return nums;
    };
    const c = parse(current);
    const d = parse(deployed);
    if (!c || !d) return false;
    const len = Math.max(c.length, d.length);
    for (let i = 0; i < len; i++) {
        const cv = c[i] ?? 0;
        const dv = d[i] ?? 0;
        if (dv > cv) return true;
        if (dv < cv) return false;
    }
    return false;
};

/**
 * true nur, wenn `deployed` ein echt spaeterer Build-Zeitstempel als `current`
 * ist. Aeltere oder gleiche Builds (Rollback!) und unparsebare Werte ergeben
 * false — sonst koennte ein stale Cache eine Reload-Schleife erzeugen.
 */
export const isNewerBuild = (current: string | undefined, deployed: string | undefined | null): boolean => {
    if (!current || !deployed) return false;
    const c = Date.parse(current);
    const d = Date.parse(deployed);
    if (!Number.isFinite(c) || !Number.isFinite(d)) return false;
    return d > c;
};

const RELOAD_BACKOFF_MS = 10 * 60 * 1000;

/**
 * Persistenter Reload-Backoff: erlaubt höchstens einen automatischen Reload
 * pro Backoff-Fenster, über Seiten-Neuladungen hinweg (Storage). Verhindert
 * Endlos-Reload-Schleifen, wenn der Client nach dem Reload weiterhin ein
 * veraltetes Bundle aus dem Cache erhält.
 */
export const shouldAutoReload = (
    storage: Pick<Storage, 'getItem' | 'setItem'>,
    nowMs: number,
    backoffMs: number = RELOAD_BACKOFF_MS,
): boolean => {
    try {
        const last = Number(storage.getItem('versionGate.lastReload'));
        if (Number.isFinite(last) && last > 0 && nowMs - last < backoffMs) return false;
        storage.setItem('versionGate.lastReload', String(nowMs));
        return true;
    } catch {
        return false;
    }
};

export type OutdatedDecision = 'reload' | 'defer' | 'skip';

/**
 * Entscheidet, wie auf eine erkannte neue Version reagiert wird:
 * - Editor offen oder Speichern läuft → 'defer' (Banner statt hartem Reload,
 *   damit ungespeicherte Berichts-Eingaben nicht verloren gehen). Der
 *   Backoff wird dabei bewusst NICHT verbraucht.
 * - sonst 'reload', gedrosselt über shouldAutoReload; im Backoff-Fenster 'skip'.
 */
export const decideOnOutdated = (
    editorOpen: boolean,
    saving: boolean,
    storage: Pick<Storage, 'getItem' | 'setItem'>,
    nowMs: number,
): OutdatedDecision => {
    if (editorOpen || saving) return 'defer';
    return shouldAutoReload(storage, nowMs) ? 'reload' : 'skip';
};

/**
 * Woher der Befund „dieses Bundle ist veraltet" kommt.
 *
 * 'deployment' = das Version-Gate hat in /version.json einen neueren Stand
 *                gesehen. Das ist eine BEOBACHTUNG dieses Tabs, und sie
 *                trifft die ganze Flotte gleichzeitig nur dann, wenn wirklich
 *                ausgeliefert wurde.
 * 'riegel'     = der Server hat abgewiesen (`BUILD_VERALTET`) oder meldet
 *                einen Mindest-Build ueber dem eigenen. Ausgeloest hat das
 *                ein ADMIN-KLICK, zu einem Zeitpunkt, den niemand sonst
 *                kennt.
 */
export type VeraltetAnlass = 'deployment' | 'riegel';

/**
 * Was geschieht, wenn dieses Bundle als veraltet feststeht?
 *
 * ─── Warum der Riegel NIE von selbst neu laedt ────────────────────────────
 * `decideOnOutdated` kennt genau zwei Gruende, nicht neu zu laden: ein
 * offener Begehungs-Editor und ein laufendes Speichern (`editorBusyRef` in
 * App.tsx). Von einem halb getippten Rechnungsentwurf, einem offenen Angebot
 * oder einem bearbeiteten SiGe-Plan weiss es nichts. Beim Version-Gate ist
 * das vertretbar: dort reloaded jeder Tab fuer sich, nachdem ER ein neues
 * Deployment gesehen hat, und ein Deployment ist ein angekuendigter Vorgang.
 *
 * Der Riegel dagegen wird von einem Administrator per Knopfdruck gesetzt —
 * und traefe dann die ganze Flotte auf einmal, zu einem beliebigen
 * Zeitpunkt, mitten in Arbeit, von der dieser Client gar nichts weiss. Dazu
 * kommt der zweite Fall: steht `min_build` ueber dem tatsaechlich
 * ausgelieferten Bundle, laedt der Tab in genau dasselbe Bundle zurueck und
 * meldet sich sofort wieder — alle zehn Minuten, endlos.
 *
 * Gebraucht wird der Reload dafuer nicht: der Server weist ohnehin ab, und
 * `istBuildVeraltet()` haelt Speichern und Warteschlange im Client an. Es
 * bleibt beim Balken — der Anwender laedt neu, wenn es ihm passt.
 *
 * 'defer' heisst genau das: Balken zeigen, nicht neu laden, und den
 * Reload-Backoff NICHT verbrauchen.
 */
export const decideOnVeraltet = (
    anlass: VeraltetAnlass,
    editorOpen: boolean,
    saving: boolean,
    storage: Pick<Storage, 'getItem' | 'setItem'>,
    nowMs: number,
): OutdatedDecision =>
    anlass === 'riegel'
        ? 'defer'
        : decideOnOutdated(editorOpen, saving, storage, nowMs);

// ─── Der Befund bleibt stehen ────────────────────────────────────────────
//
// Bis 07.09.2026 wusste nur der Aufrufer von onOutdated, dass dieses Bundle
// veraltet ist — und der konnte nur einen Reload anstossen oder ihn
// aufschieben (Editor offen). Die Schreibpfade (saveProjectToCloud, Flush der
// Offline-Warteschlange) fragten nie nach. Ergebnis am 07.09.: ein Tab mit
// altem Build und offenem Bericht hat stundenlang alle 45 s seine
// Warteschlange geleert und dabei frische Zaehler ueberschrieben.
//
// Deshalb merkt sich das Modul den Befund selbst, fuer die Lebensdauer des
// Tabs: ein Reload setzt ihn von allein zurueck, alles andere nicht. Wer
// schreiben will, fragt istBuildVeraltet() — und laesst es dann bleiben. Die
// Arbeit geht nicht verloren, sie liegt in der Warteschlange (IndexedDB) und
// wird vom NEUEN Build mit Anker und Zusammenfuehrung hochgeladen.
let buildVeraltet = false;

/** true, sobald ein Check ein neueres Deployment gesehen hat. */
export const istBuildVeraltet = (): boolean => buildVeraltet;

/**
 * Den Befund von aussen setzen — fuer die Lebensdauer des Tabs, wie beim
 * Version-Gate selbst.
 *
 * Zwei Quellen ausser dem /version.json-Abgleich melden dasselbe:
 *   * der Server hat einen Schreibvorgang mit `BUILD_VERALTET` abgewiesen
 *     (Build-Riegel, `app_maintenance.min_build`), oder
 *   * `maintenance_status()` meldet ein `min_build`, das ueber dem eigenen
 *     `__BUILD_TIME__` liegt (istUnterMindestBuild).
 * In beiden Faellen steht fest, was der Abgleich sonst erst spaeter faende:
 * dieses Bundle darf nicht mehr schreiben. Zurueckgenommen wird das nur
 * durch einen Reload.
 *
 * ─── Was der Marker NICHT tut (bewusst) ──────────────────────────────────
 * Er haelt nur die PROJEKT-Schreibwege an (`saveProjectToCloud`, der Flush
 * der Warteschlange). Rechnung, Angebot und der Journal-Spiegel schreiben
 * weiter, obwohl der Marker steht — und das ist richtig so:
 *   * Gesetzt wird er nur in einem Bundle, das gerade LAEUFT. Ein Bundle,
 *     das den Mindest-Build unterschreitet, wird vom Server an jeder der
 *     elf Riegel-Tabellen abgewiesen; dort braucht es keinen zweiten Riegel
 *     im Client.
 *   * Ein NEUERES Bundle, das den Marker vom Version-Gate bekommen hat, ist
 *     inhaltlich vertrauenswuerdig — es kennt alle Guards. Ihm auch noch die
 *     Rechnungsstellung zu verbieten, naehme Arbeit weg, ohne etwas zu
 *     schuetzen.
 *   * Und die alten Bundles, um die es wirklich geht, erreicht der Marker
 *     ohnehin nie: sie kennen ihn nicht. Genau deshalb sitzt der harte
 *     Riegel auf dem Server.
 */
export const markiereBuildVeraltet = (): void => { buildVeraltet = true; };

/** Nur fuer Tests: der Befund ist absichtlich nicht im Betrieb zuruecknehmbar. */
export const _setzeBuildVeraltetZurueck = (): void => { buildVeraltet = false; };

/**
 * Die Meldung, die ein blockierter Schreibpfad zeigt. Ein Satz, eine
 * Handlung: neu laden. Der Stand ist lokal gesichert, das darf sie sagen.
 */
export const BUILD_VERALTET_MELDUNG =
    'Speichern blockiert: Es gibt eine neue Programmversion. Bitte laden Sie die Seite neu — '
    + 'Ihre Eingaben sind lokal gesichert und werden danach hochgeladen.';

// ─── Der Build-Riegel des Servers ─────────────────────────────────────────
//
// Bis hierher ist „veraltet" eine Selbstauskunft: der Tab hat /version.json
// gelesen und sich selbst stillgelegt. Ein Tab, der gar nicht mehr fetcht
// (Netz halb tot, Datei aus dem Cache), merkt davon nichts — und genau der
// schrieb am 07.09.2026 stundenlang weiter. Deshalb sitzt derselbe Riegel
// serverseitig: ein Trigger auf `projects` und zehn Kindtabellen weist
// Schreibvorgaenge ab, deren Request-Header `x-app-build` fehlt oder aelter
// ist als `app_maintenance.min_build`.
//
// Muster wie BAUPHASE (maintenanceLogic): ein Signalwort am Anfang der
// Meldung, Fehlercode P0001, von PostgREST als HTTP 400 ausgeliefert. Der
// Client erkennt es daran — und NUR daran.

/** Das Signalwort, mit dem der Build-Riegel des Servers abweist. */
export const BUILD_GUARD_ERROR_MARKER = 'BUILD_VERALTET';

const enthaeltBuildMarker = (value: unknown): boolean =>
    typeof value === 'string' && value.includes(BUILD_GUARD_ERROR_MARKER);

/**
 * Hat der Server einen Schreibversuch wegen eines zu alten Bundles abgewiesen?
 *
 * Dieselben vier Stellen wie `isMaintenanceWriteError`: supabase-js legt den
 * Text je nach Weg in `message`, `details`, `hint` oder `code` ab, und wer
 * den Fehler weiterwirft, macht ein `Error` daraus. Gesucht wird per
 * „enthaelt", weil PostgREST dem Text gelegentlich etwas voranstellt.
 */
export const isBuildGuardWriteError = (err: unknown): boolean => {
    if (err === null || err === undefined) return false;
    if (typeof err === 'string') return enthaeltBuildMarker(err);
    if (typeof err !== 'object') return false;
    const e = err as Record<string, unknown>;
    return enthaeltBuildMarker(e.message)
        || enthaeltBuildMarker(e.details)
        || enthaeltBuildMarker(e.hint)
        || enthaeltBuildMarker(e.code);
};

/**
 * Der Fehler, den ein blockierter Schreibpfad wirft.
 *
 * Er traegt BEIDES: den Klartext fuer den Bildschirm (`message`) und das
 * Signalwort in `code`, damit `classifySyncError` ihn weiter als Build-Riegel
 * erkennt. Ohne den Code faende die Warteschlange nur einen Fehler ohne
 * Status, stufte ihn als 'retryable' ein und haengte eine Wartezeit an einen
 * Eintrag, der auf einen Reload wartet, nicht auf eine Wiederholung.
 *
 * @param ursache Der Originalfehler des Servers, falls es einen gibt. Er
 *   wandert nach `cause`, und `status`/`hint`/`details` werden hochgezogen —
 *   sonst waere die Diagnose auf den einen Satz eingedampft, den der Anwender
 *   sieht, und niemand koennte hinterher sagen, welche Tabelle abgewiesen hat.
 */
export const buildVeraltetFehler = (ursache?: unknown): Error => {
    const fehler: Error & Record<string, unknown> = Object.assign(
        new Error(BUILD_VERALTET_MELDUNG),
        { code: BUILD_GUARD_ERROR_MARKER } as Record<string, unknown>,
    );
    if (ursache === undefined) return fehler;
    fehler.cause = ursache;
    if (ursache && typeof ursache === 'object') {
        const u = ursache as Record<string, unknown>;
        if (typeof u.status === 'number') fehler.status = u.status;
        if (typeof u.hint === 'string') fehler.hint = u.hint;
        if (typeof u.details === 'string') fehler.details = u.details;
    }
    return fehler;
};

/**
 * Der HTTP-Status, mit dem PostgREST eine `raise exception` (P0001)
 * ausliefert.
 *
 * Genau dieser eine Status — und nur er — rechtfertigt es, in den Rumpf einer
 * Antwort zu sehen. Ein 200 traegt Nutzdaten, ein 5xx einen Netz- oder
 * Serverfehler; beide dafuer zu lesen waere Aufwand auf jedem Request statt
 * auf dem einen Prozent, um das es geht.
 */
export const BUILD_GUARD_STATUS = 400;

/**
 * Traegt diese Antwort die Abweisung des Build-Riegels?
 *
 * Rein: Status und Rumpf herein, ja/nein heraus — ohne `Response`, ohne Netz,
 * ohne Modulzustand. Dieselbe „enthaelt"-Regel wie `isBuildGuardWriteError`,
 * denn PostgREST verpackt die Meldung als JSON (`{"message":"BUILD_VERALTET: …"}`)
 * und stellt ihr je nach Weg etwas voran.
 */
export const istBuildRiegelAntwort = (status: number, body: string): boolean =>
    status === BUILD_GUARD_STATUS && enthaeltBuildMarker(body);

// ─── Der Melder ───────────────────────────────────────────────────────────
//
// Bis hierher musste JEDER Schreibweg die Abweisung selbst erkennen — und
// `saveReportSnapshot`, `restoreInspectionItems`, `applyLinkTransfer`,
// `projectLinkService` und der Journal-Spiegel taten es nicht. Statt sie
// alle einzeln umzubauen (und den naechsten neuen Weg wieder zu vergessen),
// haengt der Haken am Fetch des Supabase-Clients: eine Stelle, alle Wege.
//
// Muster wie idleHold/bauphaseFlush: ein reines Modul ohne React und ohne
// DOM, die Anmeldung ist ein Aufruf, die Abmeldung der Rueckgabewert. In
// einer Komponente ist das der Rueckgabewert eines Effekts und damit auch
// bei hartem Abbau sicher.

type BuildRiegelMelder = () => void;

/** Ein Eintrag je Anmeldung — zwei gleiche Callbacks melden sich nicht gegenseitig ab. */
const buildRiegelMelder = new Set<BuildRiegelMelder>();

/**
 * Anmelden: „sag mir, wenn der Server wegen eines zu alten Bundles abgewiesen
 * hat". App.tsx haengt daran denselben Pfad wie an das Version-Gate.
 *
 * @returns Die Abmeldung. Mehrfaches Aufrufen ist harmlos.
 */
export const onBuildGuardRejected = (melder: BuildRiegelMelder): (() => void) => {
    buildRiegelMelder.add(melder);
    return () => { buildRiegelMelder.delete(melder); };
};

/**
 * Befund vermerken UND die Angemeldeten rufen.
 *
 * IDEMPOTENT, und das ist tragend: dieselbe Abweisung wird zweimal erkannt —
 * einmal hier am Fetch, einmal pfadweise (`classifySyncError`,
 * `saveProjectToCloud`). `markiereBuildVeraltet` setzt eine Flagge, die schon
 * steht; der Melder in App.tsx rechnet `decideOnOutdated`, das nach dem
 * ersten Reload im Backoff-Fenster 'skip' liefert, und setzt sonst
 * `setUpdateAvailable(true)` — denselben Wert wie beim ersten Mal, React
 * rendert dafuer nicht einmal neu. Zweimal gemeldet ist also genau so viel
 * wie einmal gemeldet.
 *
 * Ein werfender Melder darf die uebrigen nicht mitnehmen und schon gar nicht
 * den Request, in dem er gerufen wird.
 */
export const meldeBuildRiegel = (): void => {
    markiereBuildVeraltet();
    for (const melder of Array.from(buildRiegelMelder)) {
        try {
            melder();
        } catch (e) {
            console.warn('[buildRiegel] Melder fehlgeschlagen:', e);
        }
    }
};

/** Nur fuer Tests: Registratur leeren. */
export const __resetBuildRiegelMelderForTest = (): void => { buildRiegelMelder.clear(); };

// ─── Vor dem Versand frisch fragen ───────────────────────────────────────
//
// Vorfall 6051 #149 (28.09.2026): 17:55 ausgeliefert, 18:15 versendete ein
// Tab mit dem alten Bundle einen Bericht. Das Gate hatte den Tab inzwischen
// als veraltet markiert, `saveProjectToCloud` verweigerte deshalb das
// Speichern — aber Mail und Sendebericht fragen den Befund nicht ab. Die
// Mail war beim Kunden, Buchung und Archiv fehlten.
//
// Ein veralteter Tab darf deshalb gar nicht erst senden. Der Herzschlag des
// Gates prueft nur alle fuenf Minuten; der Versand fragt darum SOFORT nach,
// damit ein Deploy der letzten Minuten nicht mitten im Senden auffaellt.
// Das Gate lebt in App.tsx, der Versanddialog tief darunter — deshalb meldet
// App.tsx die Pruefung hier an, wie beim Build-Riegel-Melder oben.

let versandPruefer: (() => Promise<boolean>) | null = null;

/**
 * App.tsx meldet die Sofort-Pruefung des laufenden Gates an.
 *
 * @returns Die Abmeldung; sie nimmt nur den EIGENEN Pruefer heraus, damit
 *   ein spaeter angemeldetes Gate (Remount) nicht vom alten abgemeldet wird.
 */
export const setzeVersandPruefung = (pruefer: () => Promise<boolean>): (() => void) => {
    versandPruefer = pruefer;
    return () => { if (versandPruefer === pruefer) versandPruefer = null; };
};

/** Laenger wartet der Versand nicht auf /version.json. */
export const VERSAND_PRUEFUNG_MAX_MS = 3000;

/**
 * Ist dieser Tab veraltet — und darf deshalb NICHT versenden?
 *
 * Steht der Befund schon, sofort `true`. Sonst ein frischer Check ueber das
 * angemeldete Gate, hoechstens `maxMs` lang. Ohne Gate (DEV, Tests), ohne Netz
 * oder bei Zeitueberschreitung gilt der bisherige Befund: haengt die Pruefung,
 * soll sie den Versand nicht blockieren — ein nicht veralteter Tab speichert
 * ganz normal. Faellt der Befund erst waehrend des Sendens, bleibt der
 * Nachhol-Marker; der Versanddialog sagt dann, dass Neuladen nachholt.
 */
export const istVorVersandVeraltet = async (maxMs: number = VERSAND_PRUEFUNG_MAX_MS): Promise<boolean> => {
    if (buildVeraltet) return true;
    const pruefer = versandPruefer;
    if (!pruefer) return false;
    let uhr: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            pruefer(),
            new Promise<void>(resolve => { uhr = setTimeout(resolve, maxMs); }),
        ]);
    } catch {
        // Wie ein Netzfehler im Gate: der bisherige Befund gilt.
    } finally {
        if (uhr !== undefined) clearTimeout(uhr);
    }
    return buildVeraltet;
};

/** Nur fuer Tests: angemeldete Versand-Pruefung entfernen. */
export const __resetVersandPruefungForTest = (): void => { versandPruefer = null; };

export const createVersionGate = (opts: VersionGateOptions) => {
    const {
        currentVersion,
        currentBuildTime,
        onOutdated,
        fetchImpl = fetch,
        minIntervalMs = 5 * 60 * 1000,
        fetchTimeoutMs = 10_000,
        now = Date.now,
        versionUrl = '/version.json',
    } = opts;

    let lastCheck = -Infinity;
    // Der LAUFENDE Check, nicht nur ein Flag: wer waehrend eines Checks fragt,
    // wartet auf dessen Ergebnis, statt mit „nichts Neues" abgespeist zu
    // werden. Genau das war die Luecke beim Aufwachen eines Tabs — der
    // Fokus-Check und der Flush der Warteschlange haengen am selben
    // visibilitychange, und der Flush darf erst los, wenn der Check durch ist.
    let laufend: Promise<boolean> | null = null;

    /**
     * Antwort: ist dieses Bundle veraltet? Innerhalb von minIntervalMs wird
     * nicht neu gefetcht, sondern der letzte Befund wiederholt — ausser mit
     * `sofort`: dann fragt der Check den Server auch innerhalb der Drossel
     * (Versand, siehe `istVorVersandVeraltet`). Ein laufender Check wird auch
     * dann geteilt, nicht verdoppelt.
     */
    const checkNow = async (optionen?: { sofort?: boolean }): Promise<boolean> => {
        if (laufend) return laufend;
        const t = now();
        if (!optionen?.sofort && t - lastCheck < minIntervalMs) return buildVeraltet;
        lastCheck = t;
        laufend = (async () => {
            // Ohne Obergrenze legte ein haengender Abruf das Gate fuer die
            // Lebensdauer des Tabs still: `laufend` wuerde nie frei, und jeder
            // Herzschlag, Fokus- und Versand-Check bekaeme dasselbe haengende
            // Versprechen. Die Frist laeuft deshalb GEGEN den ganzen Abruf
            // (samt `res.json()`) und haengt nicht davon ab, dass der Abruf
            // das Abbruchsignal beachtet; das Signal raeumt nur den Request
            // weg. Abgelaufen gilt wie ein Netzfehler.
            const abbruch = typeof AbortController === 'function' ? new AbortController() : null;
            let uhr: ReturnType<typeof setTimeout> | undefined;
            const frist = new Promise<null>(resolve => {
                uhr = setTimeout(() => { abbruch?.abort(); resolve(null); }, fetchTimeoutMs);
            });
            try {
                const abruf = (async () => {
                    const res = await fetchImpl(`${versionUrl}?t=${t}`, { cache: 'no-store', signal: abbruch?.signal });
                    return res.ok ? { data: await res.json() } : null;
                })();
                const antwort = await Promise.race([abruf, frist]);
                const data = antwort?.data;
                if (antwort && (isNewerBuild(currentBuildTime, data?.buildTime) || isNewerVersion(currentVersion, data?.version))) {
                    buildVeraltet = true;
                    onOutdated();
                }
            } catch {
                // Offline/Netzfehler: still bleiben, naechster Fokus versucht es erneut.
            } finally {
                if (uhr !== undefined) clearTimeout(uhr);
                laufend = null;
            }
            return buildVeraltet;
        })();
        return laufend;
    };

    return { checkNow };
};
